/** Fixed-capacity circular buffer of audio samples, newest data overwriting oldest. */
export class RingBuffer {
  private data: Float32Array;
  private writeIndex = 0;
  private written = 0;

  constructor(capacity: number) {
    this.data = new Float32Array(Math.max(1, Math.floor(capacity)));
  }

  get capacity(): number {
    return this.data.length;
  }

  /** Samples currently retrievable, which is the capacity once it has filled. */
  get available(): number {
    return Math.min(this.written, this.data.length);
  }

  /** Total samples ever written. Useful for spotting a stalled capture. */
  get totalWritten(): number {
    return this.written;
  }

  resize(capacity: number): void {
    this.data = new Float32Array(Math.max(1, Math.floor(capacity)));
    this.clear();
  }

  clear(): void {
    this.writeIndex = 0;
    this.written = 0;
  }

  write(chunk: Float32Array): void {
    const size = this.data.length;

    // A chunk larger than the buffer: only its tail can survive.
    if (chunk.length >= size) {
      this.data.set(chunk.subarray(chunk.length - size));
      this.writeIndex = 0;
      this.written += chunk.length;
      return;
    }

    const firstPart = Math.min(chunk.length, size - this.writeIndex);
    this.data.set(chunk.subarray(0, firstPart), this.writeIndex);
    if (firstPart < chunk.length) this.data.set(chunk.subarray(firstPart), 0);
    this.writeIndex = (this.writeIndex + chunk.length) % size;
    this.written += chunk.length;
  }

  /**
   * Copy the most recent `out.length` samples into `out`, oldest first.
   * Returns false if that much has not been captured yet.
   */
  readLatest(out: Float32Array): boolean {
    const length = out.length;
    const size = this.data.length;
    if (length === 0) return true;
    if (length > size || this.available < length) return false;

    const start = (this.writeIndex - length + size) % size;
    const firstPart = Math.min(length, size - start);
    out.set(this.data.subarray(start, start + firstPart), 0);
    if (firstPart < length) out.set(this.data.subarray(0, length - firstPart), firstPart);
    return true;
  }
}
